import { useState } from "preact/hooks";

interface Props {
  // Whether the per-client config exposes a "CC the team" option. The
  // widget never sees the actual recipient addresses — only this boolean.
  ccAvailable: boolean;
  brandName: string;
  // Returns true on send success so we can collapse the form back.
  onSend: (
    email: string,
    ccTeam: boolean,
    honeypot: string,
  ) => Promise<{ ok: true } | { ok: false; error: string }>;
}

type Status =
  | { kind: "idle" }
  | { kind: "sending" }
  | { kind: "error"; message: string }
  | { kind: "sent"; sentTo: string };

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function EmailTranscript({ ccAvailable, brandName, onSend }: Props) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [ccTeam, setCcTeam] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  const canSubmit =
    status.kind !== "sending" && EMAIL_RE.test(email.trim());

  function reset() {
    setOpen(false);
    setStatus({ kind: "idle" });
    setEmail("");
    setCcTeam(false);
    setHoneypot("");
  }

  async function submit(e: Event) {
    e.preventDefault();
    if (!canSubmit) return;
    const target = email.trim();
    setStatus({ kind: "sending" });
    const result = await onSend(target, ccTeam, honeypot);
    if (result.ok) {
      // Keep the success state visible until the visitor explicitly
      // dismisses it. An auto-close timer is easy to miss — visitors who
      // look away during the send return to a collapsed form and don't
      // know whether anything happened.
      setStatus({ kind: "sent", sentTo: target });
    } else {
      setStatus({ kind: "error", message: result.error });
    }
  }

  if (!open) {
    return (
      <div class="email-bar">
        <button
          type="button"
          class="email-open"
          onClick={() => setOpen(true)}
        >
          Email me a copy of this chat
        </button>
      </div>
    );
  }

  // After a successful send, replace the entire form with a confirmation
  // panel — clearer than leaving the email input on screen, and removes
  // any ambiguity about whether the system is "ready for another send"
  // (it's not until the visitor clicks Done).
  if (status.kind === "sent") {
    return (
      <div class="email-form">
        <div class="email-status email-status--ok">
          ✓ Your chat transcript has been sent to {status.sentTo}.
        </div>
        <div class="email-actions">
          <button type="button" class="email-cancel" onClick={reset}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <form class="email-form" onSubmit={submit}>
      <label class="email-field">
        <span class="email-label">Email address</span>
        <input
          type="email"
          class="email-input"
          autocomplete="email"
          required
          value={email}
          disabled={status.kind === "sending"}
          onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
        />
      </label>

      {/*
        Honeypot. Hidden from sighted users via inline style; named so
        most autofillers will fill it. Server silent-drops requests with
        a non-empty value.
      */}
      <label
        aria-hidden="true"
        style="position:absolute;left:-9999px;top:auto;width:1px;height:1px;overflow:hidden;"
      >
        Leave this field blank
        <input
          type="text"
          tabIndex={-1}
          autocomplete="off"
          value={honeypot}
          onInput={(e) =>
            setHoneypot((e.target as HTMLInputElement).value)
          }
        />
      </label>

      {ccAvailable && (
        <label class="email-cc">
          <input
            type="checkbox"
            checked={ccTeam}
            disabled={status.kind === "sending"}
            onChange={(e) =>
              setCcTeam((e.target as HTMLInputElement).checked)
            }
          />
          <span>
            {brandName ? `Also send a copy to the ${brandName} team` : "Also send a copy to our team"}
          </span>
        </label>
      )}

      <div class="email-actions">
        <button
          type="submit"
          class="email-send"
          disabled={!canSubmit}
        >
          {status.kind === "sending" ? "Sending..." : "Send"}
        </button>
        <button
          type="button"
          class="email-cancel"
          onClick={reset}
          disabled={status.kind === "sending"}
        >
          Cancel
        </button>
      </div>

      {status.kind === "error" && (
        <div class="email-status email-status--error">{status.message}</div>
      )}
    </form>
  );
}
